import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, filter, map, of } from 'rxjs';
import { BookSelectModalActions, ScopeActions } from './scope.actions';
import { ApplicationFacade } from '@lyceo/shared/data-access';
import { isDefined } from '@lyceo/shared/util';
import { ScopeService } from '../../scope.service';
import { PractiseOverviewPageActions } from '../practise/practise.actions';
import { AuthFacade } from '@lyceo/shared/auth/data-access';
import { ModalController } from '@ionic/angular/standalone';
import { BOOK_SELECT_MODAL } from '@lyceo/lyceo-pupil-app/practise/util';
import { IonicComponentRef } from '@lyceo/shared/util-models';

import { OverlayEventDetail } from '@ionic/core';
import { BookSwitchData } from '@lyceo/lyceo-pupil-app/practise/util-models';
import { PersonUsesBookFacade } from '../person-uses-book/person-uses-book.facade';
import { PractiseOverviewFacade } from '../practise/practise-overview.facade';
import { MethodActions } from '../method/method.actions';
import { PractiseBookFacade } from '../practise/practise-book.facade';

@Injectable()
export class ScopeEffects {
  private readonly actions$ = inject(Actions);
  private readonly scopeService = inject(ScopeService);
  private readonly applicationFacade: ApplicationFacade =
    inject(ApplicationFacade);
  private readonly authFacade: AuthFacade = inject(AuthFacade);
  private readonly personUsesBookFacade = inject(PersonUsesBookFacade);
  private readonly practiseOverviewFacade: PractiseOverviewFacade = inject(
    PractiseOverviewFacade,
  );
  private readonly modalController: ModalController = inject(ModalController);
  private readonly bookSelectModal =
    inject<IonicComponentRef>(BOOK_SELECT_MODAL);
  private readonly practiseBookFacade: PractiseBookFacade =
    inject(PractiseBookFacade);

  public personScopesLoaded$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PractiseOverviewPageActions.init),
      filter(() => !this.practiseOverviewFacade.practiseOverviewLoaded()),
      map(() => ScopeActions.getPersonScopes()),
    );
  });

  public getPersonScopes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ScopeActions.getPersonScopes),
      map(() => this.authFacade.userId()),
      filter(isDefined),
      exhaustMap((personId) =>
        this.scopeService.getPersonScopes(personId).pipe(
          map((scopes) =>
            ScopeActions.getPersonScopesSuccess({ scopes: scopes }),
          ),
          catchError((error: Error) =>
            of(ScopeActions.getPersonScopesFailure({ error })),
          ),
        ),
      ),
    );
  });

  public getPersonScopesFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ScopeActions.getPersonScopesFailure),
        map(() => {
          this.applicationFacade.showToaster({
            type: 'error',
            message: 'Het laden van de scopes is niet gelukt.',
          });
        }),
      );
    },
    { dispatch: false },
  );

  public openBookSelectModal$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PractiseOverviewPageActions.selectBookClicked),
      exhaustMap(async () => {
        const modal: HTMLIonModalElement = await this.modalController.create({
          component: this.bookSelectModal,
          cssClass: 'auto-height-modal',
        });
        await modal.present();
        return modal.onDidDismiss();
      }),
      map((overlayEvent: OverlayEventDetail<BookSwitchData>) => {
        if (overlayEvent.data) {
          return BookSelectModalActions.bookSelected({
            payload: overlayEvent.data,
          });
        }

        return BookSelectModalActions.modalClosed();
      }),
    );
  });

  public switchBook$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookSelectModalActions.bookSelected),
      exhaustMap((action) => {
        const personId: string | undefined = this.authFacade.userId();
        if (!isDefined(personId)) {
          return of(
            ScopeActions.switchBookFailure({
              error: new Error('PersonId is not defined'),
            }),
          );
        }

        return this.scopeService.switchBook(personId, action.payload).pipe(
          map((scope) =>
            ScopeActions.switchBookSuccess({
              scope,
              oldPersonUsesBook:
                this.personUsesBookFacade.personUsesBookByScopeId(
                  action.payload.scopeId,
                )(),
            }),
          ),
          catchError((error: Error) =>
            of(ScopeActions.switchBookFailure({ error })),
          ),
        );
      }),
    );
  });

  public automaticSingleBookSwitches$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MethodActions.getMethodsForScopeSuccess),
      filter(() => this.practiseBookFacade.singleBookSwitches().length !== 0),
      exhaustMap(() => {
        const personId: string | undefined = this.authFacade.userId();

        if (!isDefined(personId)) {
          return of(
            ScopeActions.switchBooksFailure({
              error: new Error('PersonId is not defined'),
            }),
          );
        }

        return this.scopeService
          .switchBooks(personId, this.practiseBookFacade.singleBookSwitches())
          .pipe(
            map((scopes) => ScopeActions.switchBooksSuccess({ scopes })),
            catchError((error: Error) =>
              of(ScopeActions.switchBooksFailure({ error })),
            ),
          );
      }),
    );
  });

  public switchBookSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ScopeActions.switchBookSuccess),
        map(() => {
          this.applicationFacade.showToaster({
            type: 'success',
            message: 'Het boek is gewijzigd!',
          });
        }),
      );
    },
    { dispatch: false },
  );

  public switchBookFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ScopeActions.switchBookFailure),
        map(() => {
          this.applicationFacade.showToaster({
            type: 'error',
            message: 'Het wisselen van boek is niet gelukt.',
          });
        }),
      );
    },
    { dispatch: false },
  );
}
